import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  InputAdornment,
  Alert,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Table from "./Table";
import walletService from "../../Endpoints/BrokersWallets.js"; // Import del servicio proporcionado
import { BROKERS_WALLET_EMAIL_STORAGE_KEY } from "../Table/constants.js";
import {
  formatMoney,
  formatTransactionAmount,
} from "../../utils/WalletNumberFormatter.js";
import * as styles from "./Styles";

const WALLET_NUMBER_FORMAT = "space";

const transactionColumns = [
  { field: "transactionId", headerName: "ID", width: 90, minWidth: 55, sortable: false },
  { field: "date", headerName: "Date", width: 120, minWidth: 80 },
  { field: "type", headerName: "Type", width: 110, minWidth: 75 },
  {
    field: "amount",
    headerName: "Amount",
    width: 120,
    minWidth: 85,
    align: "left",
    renderCell: (params) => {
      const val = params?.value ?? params?.row?.amount;
      if (!val || val === "-") return "-";

      // Separa la parte numérica de la moneda (ej: "8.423424 USD" -> ["8.423424", "USD"])
      const [numStr, ...rest] = String(val).trim().split(" ");
      const rawNum = Number(numStr);

      if (!Number.isFinite(rawNum)) return val;

      // Limita a 4 decimales como máximo
      const roundedNum = parseFloat(rawNum.toFixed(4));
      const formattedNum = formatTransactionAmount(roundedNum, WALLET_NUMBER_FORMAT);

      const currency = rest.length ? ` ${rest.join(" ")}` : "";
      return `${formattedNum}${currency}`;
    },
  },
  {
    field: "allocated_volume",
    headerName: "Volume",
    width: 100,
    minWidth: 70,
    align: "left",
    renderCell: (params) => {
      const type = String(params?.row?.type ?? "").trim().toUpperCase();
      if (!type.includes("PAYOUT")) return "-";
      return params?.value || "-";
    },
  },
  {
    field: "comment",
    headerName: "Comment",
    width: 200,
    minWidth: 80,
    sortable: false,
  },
  {
    field: "transactionTicket",
    headerName: "Transaction ticket",
    width: 140,
    minWidth: 80,
    sortable: false,
  },
  {
    field: "transactionLogin",
    headerName: "Transaction login",
    width: 140,
    minWidth: 80,
    sortable: false,
  },
  { field: "server", headerName: "Server", width: 140, minWidth: 75, sortable: false },
];

const PERIOD_OPTIONS = [
  { value: "all_history", label: "All history" },
  { value: "today", label: "Today" },
  { value: "last_3_days", label: "Last 3 days" },
  { value: "last_week", label: "Last week" },
  { value: "last_month", label: "Last month" },
  { value: "last_3_months", label: "Last 3 months" },
  { value: "last_6_months", label: "Last 6 months" },
  { value: "custom", label: "Custom" },
];

function formatDateInputUTC(date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getTodayUtcDate() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

function computeUtcDateRangeForPeriod(period, registrationDate = null) {
  const utcToday = getTodayUtcDate();
  const end = new Date(utcToday);
  const start = new Date(utcToday);

  switch (period) {
    case "today":
      return { fromDate: formatDateInputUTC(start), toDate: formatDateInputUTC(end) };

    case "last_3_days":
      start.setUTCDate(start.getUTCDate() - 2);
      return { fromDate: formatDateInputUTC(start), toDate: formatDateInputUTC(end) };

    case "last_week":
      start.setUTCDate(start.getUTCDate() - 6);
      return { fromDate: formatDateInputUTC(start), toDate: formatDateInputUTC(end) };

    case "last_month":
      start.setUTCMonth(start.getUTCMonth() - 1);
      return { fromDate: formatDateInputUTC(start), toDate: formatDateInputUTC(end) };

    case "last_3_months":
      start.setUTCMonth(start.getUTCMonth() - 3);
      return { fromDate: formatDateInputUTC(start), toDate: formatDateInputUTC(end) };

    case "last_6_months":
      start.setUTCMonth(start.getUTCMonth() - 6);
      return { fromDate: formatDateInputUTC(start), toDate: formatDateInputUTC(end) };

    case "all_history": {
      let regStart = null;
      if (registrationDate) {
        const parsed = new Date(registrationDate);
        if (!Number.isNaN(parsed.getTime())) {
          regStart = new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
        }
      }
      const defaultStart = regStart || new Date(Date.UTC(1970, 0, 1));
      return { fromDate: formatDateInputUTC(defaultStart), toDate: formatDateInputUTC(end) };
    }

    default:
      return { fromDate: "", toDate: "" };
  }
}

function utcDateStringToUnixRange(fromDate, toDate) {
  const utcToday = getTodayUtcDate();

  const fromUnix = fromDate
    ? Math.floor(new Date(`${fromDate}T00:00:00.000Z`).getTime() / 1000)
    : Math.floor(utcToday.getTime() / 1000);

  const toUnix = toDate
    ? Math.floor(new Date(`${toDate}T23:59:59.999Z`).getTime() / 1000)
    : Math.floor(new Date(Date.UTC(utcToday.getUTCFullYear(), utcToday.getUTCMonth(), utcToday.getUTCDate(), 23, 59, 59)).getTime() / 1000);

  return { from: fromUnix, to: toUnix };
}

function parseWalletDate(value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw === "-") return 0;
  const parsed = new Date(raw.replace(/\//g, "-").replace(" ", "T") + "Z");
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function mapWalletTransaction(item, index) {
  const rawDate = String(item?.date ?? "").trim();
  const amountValue = item?.amount;
  const rawVolume = item?.allocated_volume;
  const transactionType = String(item?.transaction_type ?? "").trim();
  const isPayout = transactionType.toUpperCase().includes("PAYOUT");
  const displayVolume =
    !isPayout || rawVolume === undefined || rawVolume === null || rawVolume === ""
      ? "-"
      : String(rawVolume);
  const currency = String(item?.ib_currency ?? "").trim();
  const displayAmount =
    amountValue === undefined || amountValue === null || amountValue === ""
      ? "-"
      : String(amountValue);
  const fallbackId = index + 1;
  const rowId = item?.id ?? fallbackId;

  return {
    gridId: rowId,
    transactionId: String(rowId),
    date: rawDate || "-",
    type: transactionType || "-",
    amount: currency && displayAmount !== "-" ? `${displayAmount} ${currency}` : displayAmount,
    amountRaw: amountValue,
    amountValue: Number(amountValue),
    currency,
    allocated_volume: displayVolume,
    allocatedVolumeValue: Number(rawVolume),
    comment: String(item?.comment ?? "").trim() || "-",
    transactionTicket: String(item?.trade_ticket ?? "").trim() || "-",
    transactionLogin: String(item?.trade_login ?? "").trim() || "-",
    server: String(item?.trading_server ?? "").trim() || "-",
    searchValue: [
      item?.id,
      item?.date,
      item?.transaction_type,
      item?.amount,
      item?.allocated_volume,
      item?.comment,
      item?.trade_ticket,
      item?.trade_login,
      item?.trading_server,
      item?.ib_currency,
    ]
      .filter(Boolean)
      .join(" "),
    registrationDate: rawDate,
  };
}

export default function BrokersWallets() {
  const { email: paramEmail } = useParams();
  const navigate = useNavigate();
  const routeEmail = decodeURIComponent(paramEmail || "").trim();
  const storedEmail = (() => {
    try {
      return String(localStorage.getItem(BROKERS_WALLET_EMAIL_STORAGE_KEY) || "").trim();
    } catch {
      return "";
    }
  })();
  const email = routeEmail || storedEmail;

  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [error, setError] = useState("");

  const [rows, setRows] = useState([]);

  // Header & Meta State
  const [walletFor, setWalletFor] = useState("—");
  const [balance, setBalance] = useState(0);
  const [currency, setCurrency] = useState("");
  const [registrationDate, setRegistrationDate] = useState(null);

  // Filters UI (draft)
  const [searchDraft, setSearchDraft] = useState("");
  const [periodDraft, setPeriodDraft] = useState("today");
  const [fromDraft, setFromDraft] = useState("");
  const [toDraft, setToDraft] = useState("");

  // Applied filters
  const [appliedSearch, setAppliedSearch] = useState("");
  const [sortField, setSortField] = useState("amount");
  const [sortDirection, setSortDirection] = useState("desc");

  // 1. Carga de información real del cliente (Balance y Datos)
  const loadClientDetails = async () => {
    if (!email) return;
    setLoadingUser(true);
    try {
      const res = await walletService.getClientByEmail(email);

      // res.data trae la información general del cliente
      const clientData = res?.data ?? {};
      const wallet = clientData?.wallet ?? {};

      // Setea el balance general de la cuenta
      setWalletFor(String(clientData?.name ?? clientData?.full_name ?? clientData?.email ?? email).trim());
      setBalance(Number(wallet?.balance ?? clientData?.balance ?? 0));
      setCurrency(String(wallet?.currency ?? clientData?.currency ?? "USD"));

      if (clientData?.registration_date) {
        setRegistrationDate(clientData.registration_date);
      }
    } catch (e) {
      console.error("LOAD CLIENT BY EMAIL ERROR:", e);
    } finally {
      setLoadingUser(false);
    }
  };

  // 2. Carga del historial de transacciones
  const loadTransactions = async ({ fromDate = "", toDate = "" } = {}) => {
    if (!email) {
      setRows([]);
      setError("No email found to load wallet.");
      return;
    }

    const unixRange = utcDateStringToUnixRange(fromDate, toDate);

    setLoading(true);
    setError("");

    try {
      const res = await walletService.getWalletTransactions(email, {
        from: unixRange.from,
        to: unixRange.to,
      });

      const payload = res?.data ?? {};
      const historyItems = payload?.history ?? [];

      // IMPORTANTE: No tocar setBalance ni setWalletFor aquí
      setRows(historyItems.map(mapWalletTransaction));
    } catch (e) {
      console.error("LOAD WALLET TRANSACTIONS ERROR:", e);
      setError(e?.message || "Error loading wallet transactions.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // 3. useEffect Inicial: Llama a AMBAS funciones
  useEffect(() => {
    if (!email) return;

    // Llama a /admin/client/{email} para obtener el balance real siempre
    loadClientDetails();

    // Llama a /admin/wallet para obtener el historial
    const initialRange = computeUtcDateRangeForPeriod("today");
    setFromDraft(initialRange.fromDate);
    setToDraft(initialRange.toDate);
    loadTransactions(initialRange);
  }, [email]);

  useEffect(() => {
    return () => {
      try {
        localStorage.removeItem(BROKERS_WALLET_EMAIL_STORAGE_KEY);
      } catch (storageError) {
        console.error("Could not clear wallet email from localStorage:", storageError);
      }
    };
  }, []);

  const filtered = useMemo(() => {
    let data = [...rows];

    const q = appliedSearch.trim().toLowerCase();
    if (q) {
      data = data.filter((row) =>
        String(row.searchValue ?? "")
          .toLowerCase()
          .includes(q)
      );
    }

    return data.sort((a, b) => {
      if (sortField === "date") {
        const aDate = parseWalletDate(a.date);
        const bDate = parseWalletDate(b.date);
        return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
      }

      if (sortField === "amount") {
        const aAmount = Number.isFinite(a.amountValue)
          ? a.amountValue
          : Number.NEGATIVE_INFINITY;
        const bAmount = Number.isFinite(b.amountValue)
          ? b.amountValue
          : Number.NEGATIVE_INFINITY;

        return sortDirection === "asc"
          ? aAmount - bAmount
          : bAmount - aAmount;
      }

      const comparison = String(a?.[sortField] ?? "").localeCompare(
        String(b?.[sortField] ?? ""),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      );

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [rows, appliedSearch, sortField, sortDirection]);

  const transactionTotalsByCurrency = useMemo(() => {
    const totals = new Map();

    filtered.forEach((row) => {
      const transactionType = String(row?.type ?? "").trim().toUpperCase();
      const rowCurrency = String(row?.currency ?? "").trim() || currency || "";

      if (!totals.has(rowCurrency)) {
        totals.set(rowCurrency, {
          currency: rowCurrency,
          payout: 0,
          withdraw: 0,
          deposit: 0,
        });
      }

      const current = totals.get(rowCurrency);
      const value = Number(row?.amountValue);

      if (!Number.isFinite(value)) return;

      if (transactionType.includes("PAYOUT")) {
        current.payout += value;
      } else if (transactionType.includes("WITHDRAW")) {
        current.withdraw += value;
      } else if (transactionType.includes("DEPOSIT")) {
        current.deposit += value;
      }
    });

    return Array.from(totals.values());
  }, [filtered, currency]);

  const displayRows = useMemo(
    () =>
      filtered.map((row) => ({
        ...row,
        amount: Number.isFinite(row.amountValue)
          ? formatTransactionAmount(row.amount, WALLET_NUMBER_FORMAT)
          : "-",
        allocated_volume:
          String(row?.type ?? "").trim().toUpperCase().includes("PAYOUT") &&
            Number.isFinite(row.allocatedVolumeValue)
            ? formatTransactionAmount(row.allocated_volume, WALLET_NUMBER_FORMAT)
            : "-",
      })),
    [filtered]
  );

  const handleSortChange = (field) => {
    if (!["date", "type", "amount"].includes(field)) return;

    if (sortField === field) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortField(field);
    setSortDirection(["date", "amount"].includes(field) ? "desc" : "asc");
  };

  const onClear = async () => {
    const range = computeUtcDateRangeForPeriod("all_history", registrationDate);
    setSearchDraft("");
    setPeriodDraft("all_history");
    setFromDraft(range.fromDate);
    setToDraft(range.toDate);
    setAppliedSearch("");
    await loadTransactions(range);
  };

  const onPeriodChange = (event) => {
    const nextPeriod = event.target.value;
    setPeriodDraft(nextPeriod);

    if (nextPeriod === "custom") return;

    const { fromDate, toDate } = computeUtcDateRangeForPeriod(nextPeriod, registrationDate);
    setFromDraft(fromDate);
    setToDraft(toDate);
  };

  const onApply = async () => {
    setAppliedSearch(searchDraft);

    if (periodDraft === "custom") {
      if (!fromDraft || !toDraft) {
        setError("Please select both From and To dates.");
        return;
      }
      await loadTransactions({ fromDate: fromDraft, toDate: toDraft });
      return;
    }

    const { fromDate, toDate } = computeUtcDateRangeForPeriod(periodDraft, registrationDate);
    setFromDraft(fromDate);
    setToDraft(toDate);
    await loadTransactions({ fromDate, toDate });
  };

  const canApplyDateRange =
    periodDraft === "custom" ? Boolean(fromDraft && toDraft) : true;

  return (
    <Box sx={{ px: 4, pt: 2, pb: 4, height: "100vh", width: "100%" }}>
      {/* BACK */}
      <Button
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
        onClick={() => navigate(-1)}
        sx={{ textTransform: "none", mb: 1.5, color: "#111827" }}
      >
        Go back
      </Button>

      <Typography variant="h4" sx={{ mb: 2 }}>
        Wallet
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {/* HEADER */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          backgroundColor: "#EEF6FF",
          display: "flex",
          gap: 4,
          alignItems: "center",
          flexWrap: "wrap",
          width: "28%",
        }}
      >
        <Typography sx={{ fontSize: 14 }}>
          <strong>Wallet for:</strong>{" "}
          <span style={{ opacity: 0.85 }}>{walletFor}</span>
        </Typography>

        <Typography sx={{ fontSize: 14 }}>
          <strong>Balance:</strong>{" "}
          <span style={{ opacity: 0.85 }}>
            {loadingUser
              ? "Loading..."
              : `${formatMoney(balance, WALLET_NUMBER_FORMAT)} ${currency}`.trim()}
          </span>
        </Typography>
      </Paper>

      {/* TABLE CARD */}
      <Paper
        elevation={2}
        sx={{
          ...styles.MainCard__body,
          padding: "20px",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "visible",
          flexGrow: 0,
          flexShrink: 0,
          alignSelf: "flex-start",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          CRM Transaction
        </Typography>

        {/* FILTERS */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search transaction"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            sx={{
              minWidth: 320,
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
              Period of time
            </Typography>
            <TextField
              select
              size="small"
              value={periodDraft}
              onChange={onPeriodChange}
              sx={{ minWidth: 180 }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {periodDraft === "custom" ? (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                  From
                </Typography>
                <TextField
                  size="small"
                  type="date"
                  value={fromDraft}
                  onChange={(e) => setFromDraft(e.target.value)}
                  sx={{ minWidth: 180 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                  To
                </Typography>
                <TextField
                  size="small"
                  type="date"
                  value={toDraft}
                  onChange={(e) => setToDraft(e.target.value)}
                  sx={{ minWidth: 180 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </>
          ) : null}

          <Button
            variant="outlined"
            onClick={onClear}
            disabled={loading}
            sx={{
              height: 40,
              minWidth: 118,
              borderRadius: "14px",
              border: "none",
              color: "#7B8490",
              backgroundColor: "rgba(255, 255, 255, 0)",
              fontSize: 16,
              fontWeight: 400,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                border: "none",
                backgroundColor: "rgba(255, 255, 255, 0)",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                border: "none",
                color: "#AAB2BC",
              },
            }}
            startIcon={<RefreshIcon sx={{ fontSize: 18, color: "#7B8490" }} />}
          >
            Clear
          </Button>

          <Button
            variant="contained"
            onClick={onApply}
            disabled={loading || !canApplyDateRange}
            sx={{
              height: 40,
              minWidth: 116,
              borderRadius: "14px",
              backgroundColor: canApplyDateRange ? "#EB0000" : "#E9E7E5",
              color: canApplyDateRange ? "#FFFFFF" : "#A9A39E",
              fontSize: 16,
              fontWeight: 400,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: canApplyDateRange ? "#D50000" : "#E9E7E5",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E9E7E5",
                color: "#A9A39E",
              },
            }}
          >
            Apply
          </Button>

          {loading ? <CircularProgress size={20} /> : null}
        </Box>

        <Table
          rows={displayRows}
          columns={transactionColumns}
          loading={loading}
          styles={styles}
          checkboxSelection={false}
          tableMinWidth={0}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          footerLeadingContent={
            <>
              {transactionTotalsByCurrency.length ? (
                transactionTotalsByCurrency.map(
                  ({ currency: summaryCurrency, payout, withdraw, deposit }) => (
                    <React.Fragment key={summaryCurrency}>
                      <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                        Total Payout {summaryCurrency}:&nbsp;
                        <b style={{ color: "#111827" }}>
                          {formatMoney(payout, WALLET_NUMBER_FORMAT)}
                        </b>
                      </Typography>

                      <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                        Total Withdraw {summaryCurrency}:&nbsp;
                        <b style={{ color: "#8B5CF6" }}>
                          -{formatMoney(withdraw, WALLET_NUMBER_FORMAT)}
                        </b>
                      </Typography>

                      <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                        Total Deposit {summaryCurrency}:&nbsp;
                        <b style={{ color: "#111827" }}>
                          {formatMoney(deposit, WALLET_NUMBER_FORMAT)}
                        </b>
                      </Typography>
                    </React.Fragment>
                  )
                )
              ) : (
                <>
                  <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    Total Payout:&nbsp;
                    <b style={{ color: "#111827" }}>0.00</b>
                  </Typography>

                  <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    Total Withdraw:&nbsp;
                    <b style={{ color: "#8B5CF6" }}>0.00</b>
                  </Typography>

                  <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    Total Deposit:&nbsp;
                    <b style={{ color: "#111827" }}>0.00</b>
                  </Typography>
                </>
              )}
            </>
          }
        />
      </Paper>
    </Box>
  );
}